/**
 * Facebook Pixel Event Tracking Utilities
 * 
 * Configuration is loaded dynamically from the backend admin settings.
 * Fallback: Uses hardcoded Pixel ID if backend config not available.
 * 
 * Standard Events:
 * - CompleteRegistration: Fired on successful signup
 * - Lead: Fired when user shows high intent (starts signup, views pricing, etc.)
 * - Login: Custom event for user login tracking
 * 
 * Usage:
 *   import { trackSignup, trackLogin, trackLead, initPixel } from '../utils/fbPixel';
 *   await initPixel(); // Call once on app load
 *   trackSignup({ email: 'user@example.com' });
 */

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Pixel configuration - loaded from backend
let pixelConfig = {
  pixel_id: null,
  enabled: false,
  events: []
};

// Fallback Pixel ID (used if backend config not available)
const FALLBACK_PIXEL_ID = '1425815291009956';

/**
 * Initialize the Facebook Pixel with configuration from backend
 * Call this once when the app loads
 */
export const initPixel = async () => {
  try {
    const response = await fetch(`${API_URL}/api/admin/pixel/public-config`);
    if (response.ok) {
      const config = await response.json();
      pixelConfig = config;
      
      if (config.enabled && config.pixel_id) {
        // Initialize Facebook Pixel with dynamic ID
        initFacebookPixel(config.pixel_id);
        console.log('[FB Pixel] Initialized with ID:', config.pixel_id);
      } else {
        console.log('[FB Pixel] Pixel is disabled or no ID configured');
      }
    } else {
      // Fallback to hardcoded pixel
      console.log('[FB Pixel] Using fallback configuration');
      initFacebookPixel(FALLBACK_PIXEL_ID);
      pixelConfig = { pixel_id: FALLBACK_PIXEL_ID, enabled: true, events: [] };
    }
  } catch (error) {
    console.warn('[FB Pixel] Failed to load config, using fallback:', error.message);
    initFacebookPixel(FALLBACK_PIXEL_ID);
    pixelConfig = { pixel_id: FALLBACK_PIXEL_ID, enabled: true, events: [] };
  }
};

/**
 * Initialize the Facebook Pixel script
 */
const initFacebookPixel = (pixelId) => {
  if (typeof window === 'undefined') return;
  
  // Check if already initialized
  if (window.fbq) {
    // Re-init with new pixel ID if different
    window.fbq('init', pixelId);
    return;
  }
  
  // Facebook Pixel base code
  (function(f, b, e, v, n, t, s) {
    if (f.fbq) return;
    n = f.fbq = function() {
      n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
    };
    if (!f._fbq) f._fbq = n;
    n.push = n;
    n.loaded = !0;
    n.version = '2.0';
    n.queue = [];
    t = b.createElement(e);
    t.async = !0;
    t.src = v;
    s = b.getElementsByTagName(e)[0];
    s.parentNode.insertBefore(t, s);
  })(window, document, 'script', 'https://connect.facebook.net/en_US/fbevents.js');
  
  window.fbq('init', pixelId);
  window.fbq('track', 'PageView');
};

/**
 * Get event configuration from backend settings
 */
const getEventConfig = (eventName) => {
  if (!pixelConfig.events || pixelConfig.events.length === 0) {
    // Default config
    return { enabled: true, currency: 'USD', value: 0.00 };
  }
  
  const eventConfig = pixelConfig.events.find(e => e.event_name === eventName);
  return eventConfig || { enabled: true, currency: 'USD', value: 0.00 };
};

/**
 * Check if pixel is enabled and event is enabled
 */
const isEventEnabled = (eventName) => {
  if (!pixelConfig.enabled || !pixelConfig.pixel_id) {
    return false;
  }
  
  const eventConfig = getEventConfig(eventName);
  return eventConfig.enabled !== false;
};

// Check if fbq (Facebook Pixel) is available
const fbq = (...args) => {
  if (typeof window !== 'undefined' && window.fbq) {
    window.fbq(...args);
    // Debug log in development
    if (process.env.NODE_ENV === 'development') {
      console.log('[FB Pixel]', ...args);
    }
  } else {
    console.warn('[FB Pixel] fbq not available - pixel may be blocked by ad blocker');
  }
};

/**
 * Track successful user signup/registration
 * @param {Object} params - Optional parameters
 * @param {string} params.email - User email (will be hashed by FB)
 * @param {string} params.method - Signup method ('email' | 'google')
 * @param {string} params.source - Traffic source if available
 */
export const trackSignup = (params = {}) => {
  // Track CompleteRegistration - ALWAYS send value and currency for Facebook compliance
  // Value is 0.00 because registration is free
  if (isEventEnabled('CompleteRegistration')) {
    fbq('track', 'CompleteRegistration', {
      content_name: 'UnCloned Journal',
      status: 'completed',
      method: params.method || 'email',
      currency: 'USD',
      value: 0.00,
      ...(params.source && { traffic_source: params.source }),
    });
    console.log('[FB Pixel] CompleteRegistration fired with value: 0.00 USD');
  }
  
  // Also track as Lead for optimization
  if (isEventEnabled('Lead')) {
    fbq('track', 'Lead', {
      content_name: 'New User Signup',
      content_category: 'registration',
      currency: 'USD',
      value: 0.00,
    });
  }
};

/**
 * Track user login
 * @param {Object} params - Optional parameters
 * @param {string} params.method - Login method ('email' | 'google')
 */
export const trackLogin = (params = {}) => {
  if (!isEventEnabled('Login')) return;
  
  fbq('trackCustom', 'Login', {
    content_name: 'User Login',
    method: params.method || 'email',
  });
};

/**
 * Track lead generation events (high-intent actions)
 * @param {Object} params - Event parameters
 * @param {string} params.action - What triggered the lead (e.g., 'started_signup', 'viewed_pricing')
 */
export const trackLead = (params = {}) => {
  if (!isEventEnabled('Lead')) return;
  
  const config = getEventConfig('Lead');
  const eventValue = typeof params.value === 'number' ? params.value : (typeof config.value === 'number' ? config.value : 0.00);
  fbq('track', 'Lead', {
    content_name: params.action || 'High Intent Action',
    content_category: params.category || 'engagement',
    currency: config.currency ?? 'USD',
    value: eventValue,
  });
};

/**
 * Track page view (useful for SPA navigation)
 */
export const trackPageView = () => {
  if (!isEventEnabled('PageView')) return;
  fbq('track', 'PageView');
};

/**
 * Track when user starts the signup flow (but hasn't completed)
 */
export const trackInitiateSignup = () => {
  if (!isEventEnabled('InitiateCheckout')) return;
  
  const config = getEventConfig('InitiateCheckout');
  fbq('track', 'InitiateCheckout', {
    content_name: 'Started Signup Flow',
    content_category: 'registration',
    currency: config.currency ?? 'USD',
    value: typeof config.value === 'number' ? config.value : 0.00,
  });
};

/**
 * Track B2B demo request or inquiry
 * @param {Object} params - Optional parameters
 * @param {string} params.client_name - B2B client name
 */
export const trackB2BLead = (params = {}) => {
  if (!isEventEnabled('B2BLead')) return;
  
  const config = getEventConfig('B2BLead');
  fbq('track', 'Lead', {
    content_name: 'B2B Demo Request',
    content_category: 'b2b',
    currency: config.currency ?? 'USD',
    value: typeof config.value === 'number' ? config.value : 0.00,
    ...(params.client_name && { client_name: params.client_name }),
  });
};

/**
 * Track onboarding step progression
 * @param {Object} params - Event parameters
 * @param {number} params.step - Current onboarding step (1-5)
 * @param {string} params.step_name - Human-readable step name
 */
export const trackOnboardingStep = (params = {}) => {
  if (!pixelConfig.enabled && !pixelConfig.pixel_id) return;
  
  const stepNames = {
    1: 'Name Entry',
    2: 'Emoji Selection',
    3: 'Contact Info',
    4: 'Referral Source',
    5: 'Review & Confirm'
  };
  
  fbq('trackCustom', 'OnboardingStep', {
    step: params.step,
    step_name: params.step_name || stepNames[params.step] || `Step ${params.step}`,
    content_category: 'onboarding',
  });
};

/**
 * Track when user starts the onboarding flow
 */
export const trackOnboardingStart = () => {
  if (!pixelConfig.enabled && !pixelConfig.pixel_id) return;
  
  fbq('trackCustom', 'OnboardingStart', {
    content_name: 'Onboarding Flow Started',
    content_category: 'onboarding',
  });
};

/**
 * Track onboarding completion (before final submit)
 */
export const trackOnboardingComplete = () => {
  if (!pixelConfig.enabled && !pixelConfig.pixel_id) return;
  
  fbq('trackCustom', 'OnboardingComplete', {
    content_name: 'Onboarding Flow Completed',
    content_category: 'onboarding',
  });
};

/**
 * Get current pixel configuration (for debugging)
 */
export const getPixelConfig = () => pixelConfig;

export default {
  initPixel,
  trackSignup,
  trackLogin,
  trackLead,
  trackPageView,
  trackInitiateSignup,
  trackB2BLead,
  trackOnboardingStep,
  trackOnboardingStart,
  trackOnboardingComplete,
  getPixelConfig,
};
